import { useState, useMemo, useRef, useCallback, useEffect, forwardRef } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { usePermissions } from "@/hooks/use-permissions";
import { useToast } from "@/hooks/use-toast";
import { AppShell } from "@/components/layouts/AppShell";
import { PageHeaderLayout } from "@/components/layouts/PageHeaderLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HeaderSearchInput } from "@/components/layouts/header-slots";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clock, MapPin, User, ChevronDown, ChevronUp, Search, ArrowUpDown, ArrowUp, ArrowDown, X, Heart } from "lucide-react";
import { TicketWeatherBadge } from "@/components/ticket-weather-badge";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, isSameDay, startOfWeek, endOfWeek, getDay, startOfYear, eachMonthOfInterval, endOfYear, getWeek, type Locale } from "date-fns";
import { de, enUS, tr } from "date-fns/locale";
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import type { Project, Ticket, Team, Plan } from "@mangelapp/shared";
import { cn } from "@/lib/utils";
import { OrganizationAvatar } from "@/components/organization-avatar";
import { useOrganizationSelector } from "@/hooks/use-organization-selector";
import { useCurrentOrganization } from "@/hooks/use-current-organization";
import { AccessDenied } from "@/components/AccessDenied";
import { usePriorities, type Priority } from "@/hooks/use-priorities";
import { getStatusColor } from "@/utils/color-utils";
import { useDateFormat } from '@/utils/date-formatter';
import { MiniTicketCard } from "@/components/mini-ticket-card";
import { LogoLoader } from "@/components/ui/logo-loader";
import { TicketSummaryCards, NO_STATUS_FILTER } from "@/components/ticket-summary-cards";
import { IconButtonFilter, IconButtonFilterFilled, IconButtonSave, IconButtonCancel } from "@/components/ui/icon-buttons";
import { RightSidebar, SidebarButtonGroup } from '@/components/ui/right-sidebar';
import { Checkbox } from "@/components/ui/checkbox";
import { isProjectDateSameDay } from '@/utils/project-dates';

// Custom hook for IntersectionObserver
function useInView(options?: IntersectionObserverInit) {
  const [isInView, setIsInView] = useState(false);
  const [hasBeenInView, setHasBeenInView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting);
      if (entry.isIntersecting) {
        setHasBeenInView(true);
      }
    }, { rootMargin: '200px', ...options });

    observer.observe(element);
    return () => observer.disconnect();
  }, [options]);

  return { ref, isInView, hasBeenInView };
}

// Import PDF.js (kept for fallback rendering only)
import * as pdfjsLib from 'pdfjs-dist';
import { PlanImageCaptureService } from '@/pages/project-details/services/PlanImageCaptureService';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// Kompakte Plan-Vorschau-Komponente für Kalender-Ticketkarten
function CalendarPlanPreview({ 
  ticket, 
  projectId 
}: { 
  ticket: Ticket; 
  projectId: string;
}) {
  const { t } = useTranslation(['pages.calendar']);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const renderTaskRef = useRef<any>(null);
  const [isRendering, setIsRendering] = useState(false);
  const [generatedPreview, setGeneratedPreview] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  
  // Get plan data
  const { data: plansResponse } = useQuery({
    queryKey: ["/api/plans/project", projectId],
    queryFn: async () => {
      const response = await apiRequest(`/api/plans/project/${projectId}?limit=200&offset=0`);
      return await response.json();
    },
    enabled: !!projectId,
  });

  const plans = plansResponse?.data || [];
  
  const currentPlanLocation = ticket.planLocation;
  const plan = plans.find(p => p.id === currentPlanLocation?.planId);
  const ticketStatusSemantic = String((ticket as any).statusSemanticType || '').toLowerCase().replace('-', '_');
  const pinColorClass =
    ticketStatusSemantic === 'completed'
      ? 'bg-green-500'
      : ticketStatusSemantic === 'in_progress'
      ? 'bg-blue-500'
      : ticketStatusSemantic === 'deferred'
      ? 'bg-gray-600'
      : 'bg-red-500';
  const pinBorderTopClass =
    ticketStatusSemantic === 'completed'
      ? 'border-t-green-500'
      : ticketStatusSemantic === 'in_progress'
      ? 'border-t-blue-500'
      : ticketStatusSemantic === 'deferred'
      ? 'border-t-gray-600'
      : 'border-t-red-500';

  // Check if we have a stored preview image (preferred for performance)
  const hasPreviewImage = !!(ticket as any).planPreviewImage;
  const previewImage = (ticket as any).planPreviewImage || generatedPreview;

  // Unterstütze sowohl Bilder als auch PDFs
  const isImageFile = plan?.fileName?.toLowerCase().match(/\.(png|jpg|jpeg)$/);
  const isPdfFile = plan?.fileName?.toLowerCase().endsWith('.pdf');

  // Berechne DPI für 1:1 Skalierung
  const calculateScreenDPI = useCallback(() => {
    const testElement = document.createElement('div');
    testElement.style.width = '1in';
    testElement.style.height = '1in';
    testElement.style.position = 'absolute';
    testElement.style.left = '-100%';
    testElement.style.top = '-100%';
    testElement.style.visibility = 'hidden';
    
    document.body.appendChild(testElement);
    const dpi = testElement.offsetWidth;
    document.body.removeChild(testElement);
    
    return dpi || 96;
  }, []);

  // Berechne kompakte Plan-Größe für Kalender-Karten mit max. Höhe (Verhältnis 1:2)
  const calculateCompactSize = useCallback((plan: Plan) => {
    // Basis-Breite für Kalender-Karten (nimmt volle Kartenbreite ein)
    const cardWidth = 320;
    const maxHeight = cardWidth / 2; // Verhältnis Breite 1 : Höhe 2
    
    if (!plan.paperFormat) return { width: cardWidth, height: maxHeight };

    const paperSizes = {
      'A0': { width: 84.1, height: 118.9 },
      'A1': { width: 59.4, height: 84.1 },
      'A2': { width: 42.0, height: 59.4 },
      'A3': { width: 29.7, height: 42.0 },
      'A4': { width: 21.0, height: 29.7 }
    };

    const size = paperSizes[plan.paperFormat as keyof typeof paperSizes];
    if (!size) {
      if (plan.widthMM && plan.heightMM) {
        const widthCM = plan.widthMM / 10;
        const heightCM = plan.heightMM / 10;
        
        let width = widthCM;
        let height = heightCM;
        
        if (plan.orientation === 'landscape') {
          [width, height] = [height, width];
        }
        
        // Berechne Skalierung basierend auf dem Seitenverhältnis
        const aspectRatio = width / height;
        let finalWidth = cardWidth;
        let finalHeight = cardWidth / aspectRatio;
        
        // Begrenze Höhe auf maxHeight (1:2 Verhältnis)
        if (finalHeight > maxHeight) {
          finalHeight = maxHeight;
          finalWidth = maxHeight * aspectRatio;
        }
        
        return {
          width: Math.round(finalWidth),
          height: Math.round(finalHeight)
        };
      }
      return { width: cardWidth, height: maxHeight };
    }

    let width = size.width;
    let height = size.height;
    
    if (plan.orientation === 'landscape') {
      [width, height] = [height, width];
    }

    // Berechne Skalierung basierend auf dem Seitenverhältnis
    const aspectRatio = width / height;
    let finalWidth = cardWidth;
    let finalHeight = cardWidth / aspectRatio;
    
    // Begrenze Höhe auf maxHeight (1:2 Verhältnis)
    if (finalHeight > maxHeight) {
      finalHeight = maxHeight;
      finalWidth = maxHeight * aspectRatio;
    }
    
    return {
      width: Math.round(finalWidth),
      height: Math.round(finalHeight)
    };
  }, []);

  // PDF-Rendering für kompakte Kalender-Vorschau
  const renderCompactPDF = useCallback(async (plan: Plan) => {
    if (!canvasRef.current || !isPdfFile) return;
    
    // Cancel any ongoing render operation
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch (e) {
        // Ignore cancellation errors
      }
      renderTaskRef.current = null;
    }
    
    setIsRendering(true);
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;
    
    try {
      // console.log('📄 Loading PDF from URL:', plan.fileUrl);
      // console.log('📄 Plan object:', plan);
      
      // Check if URL is valid
      if (!plan.fileUrl) {
        throw new Error('No PDF URL provided');
      }
      
      // Handle different URL formats
      let pdfUrl = plan.fileUrl;
      let loadingTask;
      
      // Check if it's a data URL (base64 encoded PDF)
      if (pdfUrl.startsWith('data:')) {
        // Extract base64 data from data URL
        const base64Match = pdfUrl.match(/^data:.*?;base64,(.+)$/);
        if (base64Match && base64Match[1]) {
          // Convert base64 string to Uint8Array
          const binaryString = atob(base64Match[1]);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          // Use data parameter - PDF.js accepts Uint8Array directly
          loadingTask = pdfjsLib.getDocument({
            data: bytes
          });
        } else {
          // Fallback: try using the data URL directly (PDF.js might handle it)
          loadingTask = pdfjsLib.getDocument({
            url: pdfUrl
          });
        }
      } else {
        // For regular URLs, use the API endpoint if available, otherwise use the fileUrl
        if (!pdfUrl.startsWith('http')) {
          // If it's a relative path or just a filename, use the API endpoint
          if (plan.id) {
            pdfUrl = `${import.meta.env.VITE_API_URL || window.location.origin}/api/plans/${plan.id}/file`;
          } else if (pdfUrl.startsWith('/')) {
            pdfUrl = `${window.location.origin}${pdfUrl}`;
          } else {
            pdfUrl = `${window.location.origin}/uploads/${pdfUrl}`;
          }
        }
        
        console.log('📄 Processed PDF URL:', pdfUrl);
        
        // Use httpHeaders only for HTTP URLs, not for data URLs
        loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          httpHeaders: {
            'Accept': 'application/pdf'
          }
        });
      }
      
      const pdf = await loadingTask.promise;
      console.log('📄 PDF loaded successfully, pages:', pdf.numPages);
      
      if (pdf.numPages === 0) {
        throw new Error('PDF has no pages');
      }
      
      const page = await pdf.getPage(1);
      
      const compactSize = calculateCompactSize(plan);
      
      canvas.width = compactSize.width;
      canvas.height = compactSize.height;
      
      // Weißer Hintergrund
      context.fillStyle = 'white';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // PDF-Viewport berechnen
      const originalViewport = page.getViewport({ scale: 1 });
      const scaleX = compactSize.width / originalViewport.width;
      const scaleY = compactSize.height / originalViewport.height;
      const scale = Math.min(scaleX, scaleY);
      
      const scaledViewport = page.getViewport({ scale });
      
      // PDF zentriert rendern
      const offsetX = (canvas.width - scaledViewport.width) / 2;
      const offsetY = (canvas.height - scaledViewport.height) / 2;
      
      context.save();
      context.translate(offsetX, offsetY);
      
      const renderContext = {
        canvasContext: context,
        viewport: scaledViewport,
        canvas: canvas,
      };
      
      // Store the render task so we can cancel it if needed
      const renderTask = page.render(renderContext);
      renderTaskRef.current = renderTask;
      
      await renderTask.promise;
      context.restore();
      
      // Clear the render task reference after successful render
      renderTaskRef.current = null;
      
    } catch (error: any) {
      // Ignore cancellation errors
      if (error?.name === 'RenderingCancelledException' || error?.message?.includes('cancelled')) {
        return;
      }
      
      console.error('Kalender PDF-Preview Fehler:', error);
      
      // Show error message on canvas only if canvas is still valid
      if (canvasRef.current && context) {
        const compactSize = calculateCompactSize(plan);
        canvas.width = compactSize.width;
        canvas.height = compactSize.height;
        
        context.fillStyle = '#f3f4f6';
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        context.fillStyle = '#6b7280';
        context.font = '14px Arial';
        context.textAlign = 'center';
        context.fillText(t('pages.calendar:pdfLoadError'), canvas.width / 2, canvas.height / 2 - 10);
        context.fillText(t('pages.calendar:pdfInvalidFile'), canvas.width / 2, canvas.height / 2 + 10);
      }
    } finally {
      setIsRendering(false);
      renderTaskRef.current = null;
    }
  }, [isPdfFile, calculateCompactSize, t]);

  // Generate preview on-the-fly if planLocation exists but no stored image
  useEffect(() => {
    if (currentPlanLocation && !(ticket as any).planPreviewImage && !generatedPreview && !isGeneratingPreview && plan?.fileUrl) {
      setIsGeneratingPreview(true);
      PlanImageCaptureService.capturePlanSection(
        plan.fileUrl,
        currentPlanLocation,
        2, // zoom level (2x for better detail)
        { width: 400, height: 300 }, // capture size
        ticket.ticketNumber // render pin with ticket number in image
      )
        .then((result) => {
          setGeneratedPreview(result.imageData);
        })
        .catch((error) => {
          console.error('Error generating plan preview for calendar:', error);
        })
        .finally(() => {
          setIsGeneratingPreview(false);
        });
    }
  }, [currentPlanLocation, (ticket as any).planPreviewImage, generatedPreview, isGeneratingPreview, plan?.fileUrl, ticket.ticketNumber]);

  // Render PDF wenn Plan sich ändert (only as fallback if no planLocation)
  useEffect(() => {
    if (plan && isPdfFile && !currentPlanLocation) {
      renderCompactPDF(plan);
    }
    
    // Cleanup: cancel any ongoing render when component unmounts or plan changes
    return () => {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          // Ignore cancellation errors
        }
        renderTaskRef.current = null;
      }
    };
  }, [plan, isPdfFile, currentPlanLocation, renderCompactPDF]);
  
  if (!currentPlanLocation || !plan) {
    return null;
  }

  if (!isImageFile && !isPdfFile && !previewImage && !currentPlanLocation) {
    return null;
  }

  const compactSize = calculateCompactSize(plan);
  
  // If we have a preview image (stored or generated), use it directly
  if (previewImage) {
    return (
      <div className="relative w-full bg-white rounded overflow-hidden border border-light mb-2" style={{ height: compactSize.height }}>
        <div className="flex items-center justify-center h-full relative">
          <img 
            src={previewImage}
            alt={`Plan preview for ticket ${ticket.ticketNumber}`}
            className="w-full h-full object-cover"
            style={{
              objectFit: 'cover',
            }}
          />
          {/* Pin is now rendered directly in the image, no overlay needed */}
        </div>
        {isGeneratingPreview && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <div className="text-meta text-light">{t('pages.calendar:loadingPlan') || 'Loading...'}</div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="relative w-full bg-white rounded overflow-hidden border border-light mb-2" style={{ height: compactSize.height }}>
      {isImageFile && (
        <>
          <img 
            src={plan.fileUrl}
            alt={plan.fileName}
            className="absolute w-auto h-auto min-w-full min-h-full object-cover"
            style={{
              left: '50%',
              top: '50%',
              transform: `translate(-50%, -50%) translate(${currentPlanLocation ? (100 - currentPlanLocation.x * 0.2) : 0}px, ${currentPlanLocation ? (100 - currentPlanLocation.y * 0.2) : 0}px)`
            }}
          />
          {currentPlanLocation && (
            <div 
              className="absolute pointer-events-none z-20"
              style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -100%)'
              }}
            >
              <div className="relative flex flex-col items-center">
                <div className={`w-5 h-5 ${pinColorClass} rounded-full shadow-lg flex items-center justify-center border-2 border-white relative z-10`}>
                  <span className="text-white text-meta font-bold">
                    {ticket.ticketNumber}
                  </span>
                </div>
                <div 
                  className={`w-0 h-0 border-l-[5px] border-r-[5px] border-t-[7px] border-l-transparent border-r-transparent ${pinBorderTopClass} -mt-1`}
                  style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                ></div>
              </div>
            </div>
          )}
        </>
      )}
      
      {isPdfFile && (
        <>
          <div className="flex items-center justify-center h-full relative">
            <canvas 
              ref={canvasRef}
              className="pointer-events-none"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: `translate(-50%, -50%) translate(${currentPlanLocation ? (100 - currentPlanLocation.x * 0.2) : 0}px, ${currentPlanLocation ? (100 - currentPlanLocation.y * 0.2) : 0}px)`,
                transformOrigin: 'center center',
                imageRendering: 'crisp-edges',
                filter: isRendering ? 'blur(1px)' : 'none',
                opacity: isRendering ? 0.7 : 1
              }}
            />
            
            {currentPlanLocation && !isRendering && (
              <div 
                className="absolute pointer-events-none z-20"
                style={{
                  left: '50%',
                  top: '50%',
                transform: 'translate(-50%, -100%)'
              }}
            >
              <div className="relative flex flex-col items-center">
                  <div className={`w-5 h-5 ${pinColorClass} rounded-full shadow-lg flex items-center justify-center border-2 border-white relative z-10`}>
                    <span className="text-white text-meta font-bold">
                      {ticket.ticketNumber}
                    </span>
                  </div>
                  <div 
                    className={`w-0 h-0 border-l-[5px] border-r-[5px] border-t-[7px] border-l-transparent border-r-transparent ${pinBorderTopClass} -mt-1`}
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          {isRendering && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
              <div className="text-meta text-light">{t('pages.calendar:loadingPlan')}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// MonthSection component for lazy-loaded month display
interface MonthSectionProps {
  month: Date;
  organizationId: string | null;
  projects: Project[];
  availableStatus: any[];
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  applyFilters: (tickets: Ticket[]) => Ticket[];
  getDayEvents: (day: Date, tickets: Ticket[]) => { projects: number; tickets: number; ticketStatuses: string[]; hasEvents: boolean };
  dateLocale: Locale;
  weekDays: string[];
}

function MonthSection({
  month,
  organizationId,
  projects,
  availableStatus,
  selectedDate,
  onSelectDate,
  applyFilters,
  getDayEvents,
  dateLocale,
  weekDays,
}: MonthSectionProps) {
  const { ref, hasBeenInView } = useInView();
  
  // Fetch tickets for this month when visible
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  
  const { data: monthTicketsResponse, isLoading } = useQuery({
    queryKey: ["/api/tickets", "month", month.getFullYear(), month.getMonth(), organizationId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('dueDateFrom', monthStart.toISOString());
      params.set('dueDateTo', monthEnd.toISOString());
      if (organizationId) {
        params.set('organizationId', organizationId);
      }
      const response = await apiRequest(`/api/tickets?${params.toString()}`);
      return await response.json();
    },
    enabled: hasBeenInView && !!organizationId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  
  const monthTickets: Ticket[] = monthTicketsResponse?.data || [];
  const filteredTickets = applyFilters(monthTickets);
  
  // Generate calendar days for this month
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startingDayOfWeek = (getDay(monthStart) + 6) % 7;
  const emptyDays = Array(startingDayOfWeek).fill(null);
  const allDays = [...emptyDays, ...calendarDays];
  
  return (
    <div ref={ref} className="mb-6">
      {/* Month header */}
      <div className="sticky top-0 z-10 bg-slate-50 py-2 px-1">
        <h3 
          className="text-lg font-semibold text-slate-900"
          style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
        >
          {format(month, "MMMM yyyy", { locale: dateLocale })}
        </h3>
      </div>
      
      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-t border-b border-light bg-white">
        {weekDays.map((day, dayIndex) => (
          <div 
            key={day} 
            className={cn(
              "text-center py-2",
              dayIndex !== 6 && "border-r border-light"
            )}
            style={{
              fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              fontSize: '12px',
              fontWeight: 500,
              color: '#666666'
            }}
          >
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar days */}
      <div className="grid grid-cols-7 bg-white border-t border-b border-light relative">
        {!hasBeenInView ? (
          // Placeholder when not yet visible
          <div className="col-span-7 h-32 flex items-center justify-center">
            <LogoLoader size="sm" />
          </div>
        ) : isLoading ? (
          // Loading state
          <div className="col-span-7 h-32 flex items-center justify-center">
            <LogoLoader size="sm" />
          </div>
        ) : (
          allDays.map((day, index) => {
            const totalRows = Math.ceil(allDays.length / 7);
            const currentRow = Math.floor(index / 7);
            const isLastRow = currentRow === totalRows - 1;
            const isFirstCellInRow = index % 7 === 0;
            
            // Get week number for first cell of each row
            const weekNumber = isFirstCellInRow && day ? getWeek(day, { weekStartsOn: 1 }) : null;
            // For empty first cells, try to get week number from next valid day in row
            const rowDays = allDays.slice(currentRow * 7, (currentRow + 1) * 7);
            const firstValidDayInRow = rowDays.find(d => d !== null);
            const displayWeekNumber = isFirstCellInRow ? (weekNumber || (firstValidDayInRow ? getWeek(firstValidDayInRow, { weekStartsOn: 1 }) : null)) : null;
            
            if (!day) {
              return (
                <div
                  key={`empty-${index}`}
                  className={cn(
                    "aspect-square w-full bg-slate-50 relative",
                    (index % 7 !== 6) && "border-r border-light",
                    !isLastRow && "border-b border-light"
                  )}
                >
                  {/* Week number on the border line */}
                  {isFirstCellInRow && displayWeekNumber && (
                    <div 
                      className="absolute -bottom-[10px] -left-[1px] bg-white px-0.5 z-10"
                      style={{
                        fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontSize: '11px',
                        fontWeight: 500,
                        color: '#000000'
                      }}
                    >
                      {displayWeekNumber}
                    </div>
                  )}
                </div>
              );
            }
            
            const dayEvents = getDayEvents(day, filteredTickets);
            const isSelected = isSameDay(day, selectedDate);
            const isCurrentMonth = isSameMonth(day, month);
            
            // Compute tickets for this day
            const dayTickets = filteredTickets.filter(
              t => t.dueDate && isSameDay(new Date(t.dueDate), day) && (t as any).statusSemanticType !== 'completed'
            );
            const visibleTickets = dayTickets.slice(0, 4);
            const remainingCount = Math.max(0, dayTickets.length - 4);
            
            return (
              <button
                key={day.toISOString()}
                onClick={() => onSelectDate(day)}
                className={cn(
                  "aspect-square w-full text-left p-1 transition-colors relative",
                  (index % 7 !== 6) && "border-r border-light",
                  !isLastRow && "border-b border-light",
                  isSelected 
                    ? "bg-blue-100 text-blue-900 shadow-inner"
                    : isToday(day)
                    ? "bg-blue-50 text-primary-blue"
                    : isCurrentMonth
                    ? "bg-white hover:bg-slate-50 text-dark"
                    : "bg-slate-50 text-black"
                )}
              >
                {/* Week number on the border line */}
                {isFirstCellInRow && displayWeekNumber && (
                  <div 
                    className="absolute -top-[10px] -left-[1px] bg-white px-0.5 z-10"
                    style={{
                      fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      fontSize: '13px',
                      fontWeight: 500,
                      color: '#000000'
                    }}
                  >
                    {displayWeekNumber}
                  </div>
                )}
                <div 
                  className="absolute bottom-1 right-1"
                  style={{
                    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    fontSize: '12px',
                    fontWeight: 700,
                    color: '#000000'
                  }}
                >
                  {format(day, 'd')}
                </div>
                {/* Project indicator */}
                {dayEvents.projects > 0 && (
                  <div className="absolute top-1 left-1 w-2 h-2 bg-green-500 rounded-full" />
                )}
                {/* Ticket pills with IDs */}
                {visibleTickets.length > 0 && (
                  <div className="absolute top-3 left-3 right-3 bottom-6 flex items-start justify-center">
                    <div className="grid grid-cols-1 gap-1 w-full">
                      {visibleTickets.map((ticket, ticketIndex) => {
                        const status = availableStatus.find(
                          (s: any) => s.id === (ticket as any).statusId,
                        );
                        const statusColor = status?.color || '#cbd5e1';
                        const isLastVisible = ticketIndex === visibleTickets.length - 1;
                        const showBadge = isLastVisible && remainingCount > 0;
                        
                        return (
                          <div
                            key={ticket.id}
                            className="relative flex items-center justify-center rounded-full px-1 min-h-[16px]"
                            style={{ backgroundColor: statusColor }}
                          >
                            <span
                              className="text-black text-[10px] font-semibold truncate"
                              style={{ lineHeight: '1' }}
                            >
                              {ticket.ticketNumber}
                            </span>
                            {showBadge && (
                              <span className="absolute -bottom-0.5 -right-0.5 min-w-[14px] h-[14px] px-0.5 bg-gray-700 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                {remainingCount}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function Calendar() {
  const { t } = useTranslation(['components.ticket', 'pages.calendar', 'pages.listen']);
  const { data: priorities = [] } = usePriorities() as { data: Priority[] | undefined };
  const { formatDate } = useDateFormat();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [, setLocation] = useLocation();
  const [ticketSearchTerm, setTicketSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [sortField, setSortField] = useState<'ticketNummer' | 'titel' | 'projektName' | 'zustand' | 'prioritaet' | 'zugewiesenAn' | 'faelligkeitsDatum' | 'verlaengerungsDatum'>('ticketNummer');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [showFilter, setShowFilter] = useState(false);
  const [selectedTicketForModal, setSelectedTicketForModal] = useState<Ticket | null>(null);

  const [filters, setFilters] = useState({
    zustand: '',
    prioritaet: '',
    project: '',
    assignee: ''
  });
  
  // State for project filter modal
  const [isProjectFilterModalOpen, setIsProjectFilterModalOpen] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [tempSelectedProjectIds, setTempSelectedProjectIds] = useState<string[]>([]);
  
  // State for year selector
  const [isYearSelectorOpen, setIsYearSelectorOpen] = useState(false);
  const yearSelectorRef = useRef<HTMLDivElement>(null);

  // Status filter from summary cards (calendar page only): null = All, NO_STATUS_FILTER = no status, else status id
  const [calendarStatusFilter, setCalendarStatusFilter] = useState<string | null>(null);
  
  // Close year selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (yearSelectorRef.current && !yearSelectorRef.current.contains(event.target as Node)) {
        setIsYearSelectorOpen(false);
      }
    };
    
    if (isYearSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isYearSelectorOpen]);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Refs for scrolling to current month
  const monthRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToCurrentMonth = useRef(false);

  const scrollToMonth = useCallback((monthIndex: number, behavior: ScrollBehavior = 'smooth') => {
    let attempts = 0;
    const maxAttempts = 12;

    const tryScroll = () => {
      const container = scrollContainerRef.current;
      const target = monthRefs.current[monthIndex];

      if (!container || !target) {
        if (attempts < maxAttempts) {
          attempts += 1;
          requestAnimationFrame(tryScroll);
        }
        return;
      }

      // Use container-relative scrolling instead of element.scrollIntoView for Safari reliability.
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = Math.max(0, container.scrollTop + (targetRect.top - containerRect.top));
      container.scrollTo({ top, behavior });
    };

    requestAnimationFrame(tryScroll);
  }, []);
  
  // Generate all months for the current year
  const monthsOfYear = useMemo(() => {
    const yearStart = startOfYear(new Date(currentYear, 0, 1));
    const yearEnd = endOfYear(new Date(currentYear, 0, 1));
    return eachMonthOfInterval({ start: yearStart, end: yearEnd });
  }, [currentYear]);
  
  
  // Scroll to current month on initial load
  useEffect(() => {
    if (hasScrolledToCurrentMonth.current) return;
    const currentMonthIndex = new Date().getMonth();
    if (currentYear === new Date().getFullYear()) {
      scrollToMonth(currentMonthIndex, 'auto');
      hasScrolledToCurrentMonth.current = true;
    }
  }, [monthsOfYear, currentYear, scrollToMonth]);
  
  // Get date-fns locale based on current i18n language
  const getDateLocale = () => {
    const lang = i18n.language || 'en';
    if (lang.startsWith('de')) return de;
    if (lang.startsWith('tr')) return tr;
    return enUS;
  };
  const dateLocale = getDateLocale();
  
  // Check if user is contractor in the selected organization
  const { isContractor } = useOrganizationSelector();
  const { organizationId } = useCurrentOrganization();
  
  // Check permissions using the new organization-based system
  const { hasPermission } = usePermissions();
  const canLikeTickets =
    hasPermission('manage_projects') ||
    hasPermission('manage_all_projects') ||
    hasPermission('manage_organization');

  // Fetch calendar stats for the selected year
  const { data: calendarStatsResponse } = useQuery({
    queryKey: ["/api/stats/calendar", currentYear, organizationId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('year', currentYear.toString());
      if (organizationId) params.set('organizationId', organizationId);
      const response = await apiRequest(`/api/stats/calendar?${params}`);
      return await response.json();
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Map API response to component format - now using buckets and allStatuses
  const calendarBuckets = useMemo(() => ({
    open: calendarStatsResponse?.data?.buckets?.open || 0,
    inProgress: calendarStatsResponse?.data?.buckets?.inProgress || 0,
    completed: calendarStatsResponse?.data?.buckets?.completed || 0,
    deferred: calendarStatsResponse?.data?.buckets?.deferred || 0,
  }), [calendarStatsResponse]);
  
  const calendarAllStatuses = useMemo(() => 
    calendarStatsResponse?.data?.all || [],
  [calendarStatsResponse]);

  // Like-Handler für Tickets
  const likeMutation = useMutation({
    mutationFn: async (ticketId: string) => {
      return apiRequest(`/api/tickets/${ticketId}/like`, {
        method: 'PATCH'
      });
    },
    onSuccess: () => {
      // Cache aktualisieren
      queryClient.invalidateQueries({ queryKey: ["/api/tickets"] });
    },
    onError: (error: any) => {
      toast({
        title: "Fehler beim Liken",
        description: error.message || "Das Ticket konnte nicht geliked werden.",
        variant: "destructive",
      });
    }
  });

  const handleTicketLike = (ticketId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Verhindert Navigation zum Ticket
    
    if (!canLikeTickets) {
      toast({
        title: t('pages.calendar:notAuthorized'),
        description: t('pages.calendar:noPermissionToLike'),
        variant: "destructive",
      });
      return;
    }

    likeMutation.mutate(ticketId);
  };

  const { data: projectsResponse } = useQuery({
    queryKey: ["/api/projects"],
    queryFn: async () => {
      const response = await apiRequest("/api/projects");
      return await response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });

  const projects = projectsResponse?.data || [];

  const { data: ticketsResponse } = useQuery({
    queryKey: ["/api/tickets"],
    queryFn: async () => {
      const response = await apiRequest("/api/tickets");
      return await response.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const tickets = ticketsResponse?.data || [];

  const { data: availableStatusResponse } = useQuery({
    queryKey: ["/api/status", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/status?organizationId=${encodeURIComponent(organizationId)}` : "/api/status";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId,
  });

  const availableStatus = availableStatusResponse?.data || [];
  const statusList = availableStatus; // Alias for consistency with project-details

  // Helper to get status display name with validation
  const getStatusDisplayName = (status: string | null | undefined, statusId?: string | null) => {
    // If statusId is provided, find the status in statusList
    if (statusId && statusList.length > 0) {
      const statusObj = statusList.find((s: any) => s.id === statusId);
      if (statusObj) {
        return statusObj.displayName || statusObj.name;
      }
      // If ID exists but not found in list, it was deleted - return '-'
      return '-';
    }

    // If status text is provided, check if it exists in statusList
    if (status && statusList.length > 0) {
      const normalized = status.toLowerCase().trim();
      const statusObj = statusList.find((s: any) =>
        s.name.toLowerCase().trim() === normalized ||
        (s.displayName || '').toLowerCase().trim() === normalized
      );
      if (statusObj) {
        return statusObj.displayName || statusObj.name;
      }
      // If not found in list, it was deleted - return '-'
      return '-';
    }
    
    return status || '-';
  };
  
  // Fetch gewerke for display validation
  const { data: gewerkeResponse } = useQuery({
    queryKey: ["/api/gewerke", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/gewerke?organizationId=${encodeURIComponent(organizationId)}` : "/api/gewerke";
      const response = await apiRequest(url);
      return await response.json();
    },
    enabled: !!organizationId,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
  });
  
  const gewerke = gewerkeResponse?.data || [];
  
  // Helper to get gewerk display name with validation
  const getGewerkDisplayName = (gewerk: string | null | undefined, gewerkId?: string | null) => {
    if (!gewerk && !gewerkId) return null; // Return null for calendar (won't display)
    
    // If gewerkId is provided, find the gewerk in gewerke list
    if (gewerkId && gewerke.length > 0) {
      const gewerkObj = gewerke.find((g: any) => g.id === gewerkId);
      if (gewerkObj) {
        return gewerkObj.name;
      }
      // If ID exists but not found in list, it was deleted - return null
      return null;
    }
    
    // If gewerk text is provided, check if it exists in gewerke list
    if (gewerk && gewerke.length > 0) {
      const gewerkObj = gewerke.find((g: any) => g.name.toLowerCase().trim() === gewerk.toLowerCase().trim());
      if (gewerkObj) {
        return gewerkObj.name;
      }
      // If not found in list, it was deleted - return null
      return null;
    }
    
    return gewerk || null;
  };

  // Priority helpers: resolve by priorityId first, then fallback to text
  // Uses displayName from API (database-backed translations)
  const getPriorityInfo = (ticket: any) => {
    // Try priorityId first (new structure)
    if (ticket.priorityId && priorities.length > 0) {
      const priority = priorities.find(p => p.id === ticket.priorityId);
      if (priority) {
        return {
          // Use displayName from API (database-backed translation) with fallback to name
          name: priority.displayName || priority.name,
          color: priority.color || '#6b7280',
        };
      }
      // If priorityId exists but priority not found (deleted), return '-'
      if (ticket.priorityId) {
        return {
          name: '-',
          color: '#6b7280',
        };
      }
    }
    
    // Fallback to legacy priority text (only if priorityId is null/undefined)
    const raw = ticket.priority || ticket.prioritaet || '';
    if (raw) {
      // If priorities list is loaded, try to find matching priority for color and translation
      if (priorities.length > 0) {
        const priority = priorities.find(p => p.name.toLowerCase() === raw.toLowerCase());
        if (priority) {
          return {
            // Use displayName from API (database-backed translation) with fallback to name
            name: priority.displayName || priority.name,
            color: priority.color || '#6b7280',
          };
        }
      }
      
      // Display the priority name immediately even if priorities list not loaded yet
      // Backend now returns resolved priority name, so we can show it right away
      // Just use a default color until priorities list loads
      return {
        name: raw,
        color: '#6b7280',
      };
    }
    
    // Fallback to old mapping (only for legacy default priorities)
    const getPriorityCode = (raw?: string | null): 'low' | 'normal' | 'high' | 'critical' | null => {
      if (!raw) return null;
      const v = String(raw).toLowerCase();
      if (['niedrig','low','düşük'].includes(v)) return 'low';
      if (['mittel','normal','medium','orta'].includes(v)) return 'normal';
      if (['hoch','high','yüksek'].includes(v)) return 'high';
      if (['kritisch','critical','kritik'].includes(v)) return 'critical';
      return null;
    };
    
    const code = getPriorityCode(raw);
    const colorMap: Record<string, string> = {
      'low': '#388E3C',
      'normal': '#FBC02D',
      'high': '#F57C00',
      'critical': '#D32F2F',
    };
    const labelMap: Record<string, string> = {
      'low': t('components.ticket:priority.low'),
      'normal': t('components.ticket:priority.medium'),
      'high': t('components.ticket:priority.high'),
      'critical': t('components.ticket:priority.critical'),
    };
    
    return {
      name: code ? labelMap[code] : '-',
      color: code ? colorMap[code] : '#6b7280',
    };
  };

  const { data: teamMembersResponse } = useQuery({
    queryKey: ["/api/contractors"],
    queryFn: async () => {
      const response = await apiRequest("/api/contractors");
      return await response.json();
    },
  });

  const teamMembers = teamMembersResponse?.data || [];

  // Memoized helper to get contractor name from assignedContractorId (matching project-details implementation)
  const getContractorName = useCallback((assignedContractorId: string | null | undefined) => {
    if (!assignedContractorId) return '-';
    const contractor = teamMembers.find((m: any) => m.user_id === assignedContractorId || m.id === assignedContractorId);
    return contractor?.display_name || contractor?.email || '-';
  }, [teamMembers]);
  
  // Legacy helper for backward compatibility with assigneeName (used in filters and search)
  const getFullName = (assigneeName: string | null | undefined) => {
    if (!assigneeName) return t('pages.calendar:notAssigned') || '-';
    
    // Find team member by name
    const member = teamMembers.find(m => m.name === assigneeName);
    if (member && member.lastName) {
      return `${member.name} ${member.lastName}`;
    }
    return assigneeName; // Fallback to just the name
  };

  // Apply filters to tickets (status filter is by status id via calendarStatusFilter from summary cards)
  const applyFilters = (ticketList: Ticket[]) => {
    const priorityIdByName = new Map<string, string>();
    priorities.forEach((priority) => {
      if (priority.name) {
        priorityIdByName.set(priority.name.toLowerCase().trim(), priority.id);
      }
    });

    let result = ticketList.filter(ticket => {
      // Status filter (selected id from filter dropdown)
      if (filters.zustand && (ticket as any).statusId !== filters.zustand) return false;

      // Priority filter (ID-based)
      if (filters.prioritaet) {
        const resolvedPriorityId =
          (ticket as any).priorityId ||
          priorityIdByName.get(String((ticket as any).priority || '').toLowerCase().trim()) ||
          '';
        if (resolvedPriorityId !== filters.prioritaet) {
          return false;
        }
      }
      
      // Project filter (multi-select)
      if (selectedProjectIds.length > 0 && !selectedProjectIds.includes(ticket.projectId)) return false;
      
      // Assignee filter (ID-based)
      if (filters.assignee) {
        const assignedId = (ticket as any).assignedContractorId || '';
        if (filters.assignee === 'not_assigned') {
          if (assignedId) return false;
        } else if (assignedId !== filters.assignee) {
          return false;
        }
      }
      
      return true;
    });

    // Status filter from summary cards (calendar page): filter by status id or no-status
    if (calendarStatusFilter != null) {
      result = result.filter(ticket => {
        const statusId = (ticket as any).statusId;
        return calendarStatusFilter === NO_STATUS_FILTER
          ? statusId == null
          : statusId === calendarStatusFilter;
      });
    }

    return result;
  };

  // Filter tickets based on search term and filters
  const filteredTickets = useMemo(() => {
    let result = tickets;
    
    // Apply main filters first
    result = applyFilters(result);
    
    // Then apply search filter if search term exists
    if (ticketSearchTerm.trim()) {
      result = result.filter(ticket =>
        ticket.title.toLowerCase().includes(ticketSearchTerm.toLowerCase()) ||
        ticket.ticketNumber?.toLowerCase().includes(ticketSearchTerm.toLowerCase()) ||
        (ticket.gewerk && ticket.gewerk.toLowerCase().includes(ticketSearchTerm.toLowerCase())) ||
        (ticket.assigneeName && ticket.assigneeName.toLowerCase().includes(ticketSearchTerm.toLowerCase())) ||
        (ticket.status && ticket.status.toLowerCase().includes(ticketSearchTerm.toLowerCase())) ||
        (ticket.priority && ticket.priority.toLowerCase().includes(ticketSearchTerm.toLowerCase()))
      );
    }
    
    return result;
  }, [tickets, ticketSearchTerm, filters, selectedProjectIds, calendarStatusFilter]);

  // Fetch tickets for the selected date's month (for sidebar display)
  const selectedMonthStart = startOfMonth(selectedDate);
  const selectedMonthEnd = endOfMonth(selectedDate);
  
  const { data: selectedMonthTicketsResponse } = useQuery({
    queryKey: ["/api/tickets", "selected-month", selectedDate.getFullYear(), selectedDate.getMonth(), organizationId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('dueDateFrom', selectedMonthStart.toISOString());
      params.set('dueDateTo', selectedMonthEnd.toISOString());
      if (organizationId) {
        params.set('organizationId', organizationId);
      }
      const response = await apiRequest(`/api/tickets?${params.toString()}`);
      return await response.json();
    },
    enabled: !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
  
  const selectedMonthTickets: Ticket[] = selectedMonthTicketsResponse?.data || [];
  
  // Filter tickets for the selected date
  const selectedDateTickets = useMemo(() => {
    const ticketsToFilter = applyFilters(selectedMonthTickets);
    return ticketsToFilter.filter(ticket => 
      ticket.dueDate && isSameDay(new Date(ticket.dueDate), selectedDate)
    );
  }, [selectedMonthTickets, selectedDate, filters, ticketSearchTerm, calendarStatusFilter]);

  // Navigate calendar to ticket's due date
  const navigateToTicketDate = (ticket: Ticket) => {
    if (ticket.dueDate) {
      const ticketDate = new Date(ticket.dueDate);
      setSelectedDate(ticketDate);
      setCurrentYear(ticketDate.getFullYear());
      setCurrentMonth(ticketDate);
      
      // Scroll to the ticket's month
      scrollToMonth(ticketDate.getMonth(), 'smooth');
      
      // Open modal with ticket details
      setSelectedTicketForModal(ticket);
    }
  };

  // Konvertiert Status-Name zu Hex-Farbe und hellere Version für Karten-Hintergrund
  const getStatusCardStyles = (statusName: string) => {
    const status = availableStatus.find(s => s.name === statusName);
    if (!status) return {
      backgroundColor: '#f8fafc',
      borderLeftColor: '#cbd5e1'
    };
    
    // Nutze die direkte Hex-Farbe für den Border und eine hellere Version für den Hintergrund
    const borderColor = status.color;
    // Erstelle eine hellere Version der Farbe für den Hintergrund (füge Transparenz hinzu)
    const backgroundColor = borderColor + '20'; // Fügt 20% Transparenz hinzu
    
    return {
      backgroundColor,
      borderLeftColor: borderColor
    };
  };

  // Generate calendar days (current month only with proper alignment)
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Calculate starting position (Monday = 0, Sunday = 6)
  const startingDayOfWeek = (getDay(monthStart) + 6) % 7; // Convert Sunday=0 to Monday=0 system
  const emptyDays = Array(startingDayOfWeek).fill(null);
  const allDays = [...emptyDays, ...calendarDays];
  
  // Helper to check if this is the last day of the month in a row
  const isLastDayInRow = (index: number) => {
    // Check if this is the last actual day of the month
    const isLastDay = allDays[index] && allDays[index].getTime() === monthEnd.getTime();
    if (isLastDay) return true;
    
    // Check if it's Sunday (last day of week)
    return (index % 7 === 6);
  };

  // Helper to check if this is the first day of the month
  const isFirstDayOfMonth = (index: number) => {
    return allDays[index] && allDays[index].getTime() === monthStart.getTime();
  };

  // Get events for selected date (with filters applied)
  const selectedDateEvents = [
    ...projects
      .filter(project =>
        isProjectDateSameDay(project.startDatum, selectedDate) ||
        isProjectDateSameDay(project.endDatum, selectedDate)
      )
      .map(project => ({
        type: 'project' as const,
        title: isProjectDateSameDay(project.startDatum, selectedDate)
          ? `${t('pages.calendar:projectStart')} ${project.name}`
          : `${t('pages.calendar:projectEnd')} ${project.name}`,
        time: isProjectDateSameDay(project.startDatum, selectedDate) ? '09:00' : '17:00',
        location: project.adresse,
        manager: project.auftraggeber,
        prioritaet: 'medium', // Canonical fallback priority code for project events
        id: project.id,
        projectName: project.name
      })),
    ...applyFilters(tickets)
      .filter(ticket => ticket.dueDate && isSameDay(new Date(ticket.dueDate), selectedDate))
      .map(ticket => {
        const project = projects.find(p => p.id === ticket.projectId);
        return {
          type: 'ticket' as const,
          title: `${ticket.type || 'Mangel'}: ${ticket.title}`,
          time: format(new Date(ticket.dueDate!), 'HH:mm'),
          location: ticket.location || 'Nicht angegeben',
          reporter: ticket.reporterName,
          assigneeName: ticket.assigneeName,
          prioritaet: ticket.priority,
          zustand: getStatusDisplayName((ticket as any).status, (ticket as any).statusId),
          id: ticket.id,
          ticketNumber: ticket.ticketNumber,
          projectName: project?.name || 'Unbekanntes Projekt',
          projectId: ticket.projectId
        };
      })
  ];

  // Get day indicators (projects starting/ending, tickets created) with filters applied
  // Accepts optional ticketsList parameter for per-month ticket data
  const getDayEvents = (day: Date, ticketsList?: Ticket[]) => {
    const projectEvents = projects.filter(project =>
      isProjectDateSameDay(project.startDatum, day) ||
      isProjectDateSameDay(project.endDatum, day)
    );
    const ticketsToUse = ticketsList || applyFilters(tickets);
    const ticketEvents = ticketsToUse.filter(ticket => 
      ticket.dueDate && isSameDay(new Date(ticket.dueDate), day)
    );
    
    // Collect non-completed status identifiers for this day
    const ticketStatuses = ticketEvents
      .filter((ticket) => (ticket as any).statusSemanticType !== 'completed')
      .map(ticket => (ticket as any).statusId || (ticket as any).status)
      .slice(0, 32); // Max 4 Zeilen × 8 Felder = 32 Tickets
    
    return {
      projects: projectEvents.length,
      tickets: ticketEvents.filter(ticket => (ticket as any).statusSemanticType !== 'completed').length,
      ticketStatuses,
      hasEvents: projectEvents.length > 0 || ticketStatuses.length > 0
    };
  };

  const nextMonth = () => {
    const next = new Date(currentMonth);
    next.setMonth(next.getMonth() + 1);
    setCurrentMonth(next);
  };

  const prevMonth = () => {
    const prev = new Date(currentMonth);
    prev.setMonth(prev.getMonth() - 1);
    setCurrentMonth(prev);
  };

  // Sortier-Handler für Tabellen-Header
  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sortier-Icon Component
  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 text-light" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-3 w-3 ml-1 text-muted" />
      : <ArrowDown className="h-3 w-3 ml-1 text-muted" />;
  };

  // Block contractors from accessing calendar page (after all hooks)
  if (isContractor) {
    return (
      <AppShell>
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h1 className="text-lg font-bold text-slate-900 uppercase tracking-wide">{t('pages.calendar:title')}</h1>
              <Button variant="outline" className="bg-blue-100 text-blue-800 text-xs font-medium px-3 h-7 border-blue-200">
                {formatDate(selectedDate)}
              </Button>
            </div>
            <div className="flex items-center space-x-3">
              <OrganizationAvatar />
            </div>
          </div>
        </header>
        {/* Main Content */}
        <main className="flex-1 overflow-auto px-6 pt-0 pb-6 bg-slate-50">
          <AccessDenied />
        </main>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageHeaderLayout
        pageTitle={t('pages.calendar:title')}
        pageSubtitle={formatDate(selectedDate)}
        row3MaxWidth="900px"
        searchComponent={
          <HeaderSearchInput
            value={ticketSearchTerm}
            onChange={setTicketSearchTerm}
            placeholder={t('pages.calendar:searchPlaceholder')}
          />
        }
        ticketSummaryCards={
          <TicketSummaryCards
            buckets={calendarBuckets}
            allStatuses={calendarAllStatuses}
            onStatusClick={setCalendarStatusFilter}
            selectedStatusId={calendarStatusFilter}
          />
        }
        actionButtons={
          <div className="flex items-center gap-2">
            <Button
              variant="pillOutline"
              size="sm"
              className="bg-white text-slate-600 hover:bg-white"
              onClick={() => {
                const today = new Date();
                setCurrentYear(today.getFullYear());
                setSelectedDate(today);
                scrollToMonth(today.getMonth(), 'smooth');
              }}
            >
              {t('pages.calendar:today')}
            </Button>
            <div className="relative" ref={yearSelectorRef}>
              <Button
                variant="pillOutline"
                size="sm"
                className="bg-white text-slate-600 hover:bg-white min-w-[80px]"
                onClick={() => setIsYearSelectorOpen(!isYearSelectorOpen)}
              >
                {currentYear}
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
              {isYearSelectorOpen && (
                <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-[300px] overflow-y-auto min-w-[100px]">
                  {Array.from({ length: 26 }, (_, i) => 2025 + i).map((year) => (
                    <button
                      key={year}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-100 ${year === currentYear ? 'bg-slate-100 font-semibold' : ''}`}
                      onClick={() => {
                        setCurrentYear(year);
                        setIsYearSelectorOpen(false);
                        // Jump to the first month of the selected year.
                        scrollToMonth(0, 'smooth');
                      }}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        }
      >
        {/* Main Content */}
        <div className="flex-1 overflow-auto px-0 pt-0 pb-0 bg-white">
        <div className="p-4 pt-2 -mt-[8px]">
          <div className="flex gap-6 items-start">
            
            {/* Calendar - Links */}
            <div className="flex flex-col h-full w-[900px] min-w-[556px] flex-shrink-0">
              
              {/* Filter Dropdown - outside Card */}
              {showFilter && (
                <div className="mb-4 px-6">
                  <div className="p-3 bg-slate-50 border border-light rounded">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="section-heading">{t('pages.calendar:filterOptions')}</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setFilters({ zustand: '', prioritaet: '', project: '', assignee: '' })}
                          className="h-5 px-2 text-meta text-muted hover:text-dark"
                        >
                          <X className="h-3 w-3 mr-1" />
                          {t('pages.calendar:reset')}
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        {/* Status Filter */}
                        <div>
                          <label className="text-label block mb-0.5">{t('pages.calendar:status')}</label>
                          <select
                            value={filters.zustand}
                            onChange={(e) => setFilters(prev => ({ ...prev, zustand: e.target.value }))}
                            className="textbox-select w-full h-5"
                          >
                            <option value="">{t('pages.calendar:allStatuses')}</option>
                            {availableStatus.map(status => (
                              <option key={status.id} value={status.id}>{status.displayName || status.name}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Priority Filter */}
                        <div>
                          <label className="text-label block mb-0.5">{t('pages.calendar:priority')}</label>
                          <select
                            value={filters.prioritaet}
                            onChange={(e) => setFilters(prev => ({ ...prev, prioritaet: e.target.value }))}
                            className="textbox-select w-full h-5"
                          >
                            <option value="">{t('pages.calendar:allPriorities')}</option>
                            {priorities.map((priority) => (
                              <option key={priority.id} value={priority.id}>
                                {priority.displayName || priority.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Project Filter */}
                        <div>
                          <label className="text-label block mb-0.5">{t('pages.calendar:project')}</label>
                          <select
                            value={filters.project}
                            onChange={(e) => setFilters(prev => ({ ...prev, project: e.target.value }))}
                            className="textbox-select w-full h-5"
                          >
                            <option value="">{t('pages.calendar:allProjects')}</option>
                            {projects.map(project => (
                              <option key={project.id} value={project.id}>{project.name}</option>
                            ))}
                          </select>
                        </div>
                        
                        {/* Assignee Filter */}
                        <div>
                          <label className="text-label block mb-0.5">{t('pages.calendar:assigned')}</label>
                          <select
                            value={filters.assignee}
                            onChange={(e) => setFilters(prev => ({ ...prev, assignee: e.target.value }))}
                            className="textbox-select w-full h-5"
                          >
                            <option value="">{t('pages.calendar:allAssignments')}</option>
                            <option value="not_assigned">{t('pages.calendar:notAssigned')}</option>
                            {teamMembers.map(member => (
                              <option key={member.id} value={member.id}>
                                {member.display_name || member.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              
              {/* Calendar Card */}
              <Card className="h-[calc(100vh-300px)] flex flex-col card-container-no-shadow card-container-border-black overflow-hidden calendar-grid-container">
                <CardContent className="p-0 flex-1 overflow-hidden">
                  {viewMode === 'calendar' ? (
                    // Yearly Calendar View - Scrollable
                    <div 
                      ref={scrollContainerRef}
                      className="h-full overflow-y-auto px-2"
                    >
                      {monthsOfYear.map((month, index) => (
                        <div
                          key={month.toISOString()}
                          ref={el => { monthRefs.current[index] = el; }}
                        >
                          <MonthSection
                            month={month}
                            organizationId={organizationId}
                            projects={projects}
                            availableStatus={availableStatus}
                            selectedDate={selectedDate}
                            onSelectDate={setSelectedDate}
                            applyFilters={applyFilters}
                            getDayEvents={getDayEvents}
                            dateLocale={dateLocale}
                            weekDays={[
                              t('pages.calendar:monday'),
                              t('pages.calendar:tuesday'),
                              t('pages.calendar:wednesday'),
                              t('pages.calendar:thursday'),
                              t('pages.calendar:friday'),
                              t('pages.calendar:saturday'),
                              t('pages.calendar:sunday')
                            ]}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Listen-Ansicht im EMIN Tabellen-Layout
                    
                        <div className="overflow-x-auto">
                          <table className="min-w-full">
                            {/* Header */}
                            <thead className="sticky top-0 z-10">
                              <tr className="table-header-row">
                              <th className="table-header-cell w-16">
                                <button
                                  onClick={() => handleSort('ticketNummer')}
                                  className="flex items-center hover:text-slate-900"
                                >
                                  {t('pages.calendar:id')}
                                  <SortIcon field="ticketNummer" />
                                </button>
                              </th>
                              <th className="table-header-cell">
                                <button
                                  onClick={() => handleSort('titel')}
                                  className="flex items-center hover:text-slate-900"
                                >
                                  {t('pages.calendar:tableTitle')}
                                  <SortIcon field="titel" />
                                </button>
                              </th>
                              <th className="table-header-cell">
                                <button
                                  onClick={() => handleSort('projektName')}
                                  className="flex items-center hover:text-slate-900"
                                >
                                  {t('pages.calendar:project')}
                                  <SortIcon field="projektName" />
                                </button>
                              </th>
                              <th className="table-header-cell">
                                <button
                                  onClick={() => handleSort('zustand')}
                                  className="flex items-center hover:text-slate-900"
                                >
                                  {t('pages.calendar:status')}
                                  <SortIcon field="zustand" />
                                </button>
                              </th>
                              <th className="table-header-cell">
                                <button
                                  onClick={() => handleSort('prioritaet')}
                                  className="flex items-center hover:text-slate-900"
                                >
                                  {t('pages.calendar:priority')}
                                  <SortIcon field="prioritaet" />
                                </button>
                              </th>
                              <th className="table-header-cell">
                                <button
                                  onClick={() => handleSort('zugewiesenAn')}
                                  className="flex items-center hover:text-slate-900"
                                >
                                  {t('pages.calendar:assigned')}
                                  <SortIcon field="zugewiesenAn" />
                                </button>
                              </th>
                              <th className="table-header-cell">
                                <button
                                  onClick={() => handleSort('faelligkeitsDatum')}
                                  className="flex items-center hover:text-slate-900"
                                >
                                  {t('pages.calendar:dueDate')}
                                  <SortIcon field="faelligkeitsDatum" />
                                </button>
                              </th>
                              <th className="table-header-cell">
                                <button
                                  onClick={() => handleSort('verlaengerungsDatum')}
                                  className="flex items-center hover:text-slate-900"
                                >
                                  {t('pages.calendar:extensionDate')}
                                  <SortIcon field="verlaengerungsDatum" />
                                </button>
                              </th>
                              <th className="table-header-cell text-center w-12">
                                <span>
                                  {t('pages.calendar:weather')}
                                </span>
                              </th>
                              <th className="table-header-cell text-center w-12">
                                <span>
                                  {t('pages.calendar:likes')}
                                </span>
                              </th>
                            </tr>
                          </thead>
                          
                          {/* Body */}
                          <tbody className="bg-white">
                            {applyFilters(tickets)
                              .filter(ticket => {
                                if (!ticket.dueDate) return false;
                                const ticketDate = new Date(ticket.dueDate);
                                return isSameMonth(ticketDate, currentMonth);
                              })
                              .sort((a, b) => {
                                const project_a = projects.find(p => p.id === a.projectId);
                                const project_b = projects.find(p => p.id === b.projectId);
                                
                                let valueA: any = '';
                                let valueB: any = '';
                                
                                switch (sortField) {
                                  case 'ticketNummer':
                                    valueA = a.ticketNumber || '';
                                    valueB = b.ticketNumber || '';
                                    break;
                                  case 'titel':
                                    valueA = a.title || '';
                                    valueB = b.title || '';
                                    break;
                                  case 'projektName':
                                    valueA = project_a?.name || '';
                                    valueB = project_b?.name || '';
                                    break;
                                  case 'zustand':
                                    valueA = getStatusDisplayName((a as any).status, (a as any).statusId);
                                    valueB = getStatusDisplayName((b as any).status, (b as any).statusId);
                                    break;
                                  case 'prioritaet':
                                    valueA = a.priority || '';
                                    valueB = b.priority || '';
                                    break;
                                  case 'zugewiesenAn':
                                    valueA = getContractorName((a as any).assignedContractorId) || getFullName(a.assigneeName);
                                    valueB = getContractorName((b as any).assignedContractorId) || getFullName(b.assigneeName);
                                    break;
                                  case 'faelligkeitsDatum':
                                    valueA = a.dueDate ? new Date(a.dueDate) : new Date(0);
                                    valueB = b.dueDate ? new Date(b.dueDate) : new Date(0);
                                    break;
                                  case 'verlaengerungsDatum':
                                    valueA = a.extensionDate ? new Date(a.extensionDate) : new Date(0);
                                    valueB = b.extensionDate ? new Date(b.extensionDate) : new Date(0);
                                    break;
                                  default:
                                    valueA = a.ticketNumber || '';
                                    valueB = b.ticketNumber || '';
                                }

                                if (valueA instanceof Date && valueB instanceof Date) {
                                  const result = valueA.getTime() - valueB.getTime();
                                  return sortDirection === 'asc' ? result : -result;
                                }
                                
                                const result = String(valueA).localeCompare(String(valueB), 'de');
                                return sortDirection === 'asc' ? result : -result;
                              })
                              .map((ticket, index) => {
                                const project = projects.find(p => p.id === ticket.projectId);
                                const statusDisplayName = getStatusDisplayName((ticket as any).status, (ticket as any).statusId);
                                const statusColor = getStatusColor(
                                  (ticket as any).status,
                                  statusList,
                                  (ticket as any).statusId,
                                  (ticket as any).statusSemanticType,
                                );
                                const priorityInfo = getPriorityInfo(ticket);
                                
                                return (
                                  <tr
                                    key={ticket.id}
                                    onClick={() => setSelectedTicketForModal(ticket)}
                                    className="table-body-row cursor-pointer"
                                  >
                                    <td className="table-body-cell w-16">
                                      {ticket.ticketNumber}
                                    </td>
                                    <td className="table-body-cell truncate max-w-64">
                                      {ticket.title || t('pages.calendar:unnamed')}
                                    </td>
                                    <td className="table-body-cell truncate">
                                      {project?.name || t('pages.calendar:unknown')}
                                    </td>
                                    <td className="table-body-cell">
                                      <span style={{ color: statusColor }}>
                                        {statusDisplayName}
                                      </span>
                                    </td>
                                    <td className="table-body-cell">
                                      <span style={{ color: priorityInfo.color }}>
                                        {priorityInfo.name}
                                      </span>
                                    </td>
                                    <td className="table-body-cell truncate">
                                      {getContractorName((ticket as any).assignedContractorId) || getFullName(ticket.assigneeName)}
                                    </td>
                                    <td className="table-body-cell">
                                      {ticket.dueDate ? (
                                    <span className={new Date(ticket.dueDate) < new Date() ? "text-error font-medium" : ""}>
                                      {formatDate(ticket.dueDate)}
                                    </span>
                                      ) : (
                                        <span>-</span>
                                      )}
                                    </td>
                                    <td className="table-body-cell">
                                      {ticket.extensionDate ? (
                                        <span className={new Date(ticket.extensionDate) < new Date() ? "text-error font-medium" : ""}>
                                          {formatDate(ticket.extensionDate)}
                                        </span>
                                      ) : (
                                        <span>-</span>
                                      )}
                                    </td>
                                    <td className="table-body-cell text-center">
                                      <div className="flex items-center justify-center">
                                        <TicketWeatherBadge weatherConditions={ticket.weatherConditions} />
                                      </div>
                                    </td>
                                    <td className="table-body-cell text-center">
                                      <div className="flex items-center justify-center gap-2">
                                        {canLikeTickets ? (
                                          // User with permissions: Full like button
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handleTicketLike(ticket.id, event);
                                            }}
                                            className="p-1 h-6 w-6 hover:bg-red-100 transition-colors"
                                            title={ticket.likes && ticket.likes > 0 ? t('pages.calendar:removeLike') : t('pages.calendar:likeTicket')}
                                            disabled={likeMutation.isPending}
                                          >
                                            <Heart 
                                              className={`h-4 w-4 transition-colors ${
                                                ticket.likes && ticket.likes > 0
                                                  ? "fill-red-500 text-red-500" 
                                                  : "text-muted hover:text-red-400"
                                              }`} 
                                            />
                                          </Button>
                                        ) : (
                                          // SUB-USER: Nur anzeige, kein Klick
                                          <>
                                            {ticket.likes && ticket.likes > 0 && (
                                              <Heart className="h-4 w-4 fill-red-500 text-red-500" />
                                            )}
                                          </>
                                        )}
                                        
                                        {/* Like-Zähler - nur wenn > 0 */}
                                        {ticket.likes && ticket.likes > 0 && (
                                          <span>
                                            {ticket.likes}
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                        
                        {/* Keine Tickets Nachricht */}
                        {tickets.filter(ticket => {
                          if (!ticket.dueDate) return false;
                          const ticketDate = new Date(ticket.dueDate);
                          return isSameMonth(ticketDate, currentMonth);
                        }).length === 0 && (
                          <div className="p-8 text-center text-light">
                            <div className="text-meta">{t('pages.calendar:noTicketsFor')} {format(currentMonth, "MMMM yyyy", { locale: dateLocale })}</div>
                          </div>
                        )}
                        </div>
                      
                    
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Spacer to push right content to edge */}
            <div className="flex-1" />

            {/* Right side container - Ticket Cards + Right Sidebar */}
            {viewMode === 'calendar' && (
              <div className="flex gap-4 items-start">
                {/* Ticket Cards - Fixed width container */}
                <div className="flex flex-col h-full w-[377px]">
                  {selectedDateTickets.length > 0 ? (
                    <Card className="h-[calc(100vh-300px)] flex flex-col card-container-no-shadow card-container-border-black rounded-[24px] overflow-hidden">
                      <CardContent className="flex-1 overflow-y-auto p-0 pt-0">
                        <div className="space-y-4 flex flex-col items-end">
                          {selectedDateTickets.map((ticket) => {
                            // Get project for this ticket
                            const project = projects.find(p => p.id === ticket.projectId);
                            
                            return (
                              <div
                                key={ticket.id}
                                className="w-full flex justify-end"
                              >
                                <MiniTicketCard
                                  ticket={ticket}
                                  project={project}
                                  organizationId={organizationId}
                                  className="calendar-mini-ticket-card"
                                />
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                </div>

                {/* Right Sidebar with Filter Button */}
                <RightSidebar>
                  <SidebarButtonGroup hideLabel>
                    {selectedProjectIds.length > 0 ? (
                      <IconButtonFilterFilled
                        onClick={() => {
                          // If no filters applied, select all projects by default
                          if (selectedProjectIds.length === 0) {
                            setTempSelectedProjectIds(projects.map(p => p.id));
                          } else {
                            setTempSelectedProjectIds([...selectedProjectIds]);
                          }
                          setIsProjectFilterModalOpen(true);
                        }}
                        size={36}
                        title={t('pages.calendar:filterOptions')}
                      />
                    ) : (
                      <IconButtonFilter
                        onClick={() => {
                          // If no filters applied, select all projects by default
                          if (selectedProjectIds.length === 0) {
                            setTempSelectedProjectIds(projects.map(p => p.id));
                          } else {
                            setTempSelectedProjectIds([...selectedProjectIds]);
                          }
                          setIsProjectFilterModalOpen(true);
                        }}
                        size={36}
                        title={t('pages.calendar:filterOptions')}
                      />
                    )}
                  </SidebarButtonGroup>
                </RightSidebar>
              </div>
            )}
          </div>
        </div>
        </div>
      </PageHeaderLayout>
      {/* Project Filter Modal */}
      <Dialog open={isProjectFilterModalOpen} onOpenChange={setIsProjectFilterModalOpen}>
        <DialogContent 
          className="sm:max-w-md bg-[#DDDDDD] rounded-[24px] [&>button]:hidden"
        >
          <div className="flex items-start justify-between gap-4">
            <DialogHeader className="text-left">
              <DialogTitle className="text-sm font-semibold">{t('pages.calendar:selectProject')}</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                {tempSelectedProjectIds.length > 0 
                  ? `${tempSelectedProjectIds.length} ${t('pages.calendar:projectsSelected')}`
                  : t('pages.calendar:allProjects')
                }
              </DialogDescription>
            </DialogHeader>
            <div className="flex items-center gap-2">
              <IconButtonCancel
                onClick={() => {
                  setIsProjectFilterModalOpen(false);
                  setTempSelectedProjectIds([]);
                }}
                size={30}
              />
              <IconButtonSave
                onClick={() => {
                  // If all projects are selected, treat it as "no filter" (empty array)
                  if (tempSelectedProjectIds.length === projects.length) {
                    setSelectedProjectIds([]);
                  } else {
                    setSelectedProjectIds([...tempSelectedProjectIds]);
                  }
                  setIsProjectFilterModalOpen(false);
                }}
                size={30}
              />
            </div>
          </div>
          
          <div className="mt-4 max-h-[400px] overflow-y-auto bg-white rounded-lg">
            {projects.map((project, index) => (
              <div key={project.id}>
                <div 
                  className="flex items-center gap-3 p-3 bg-white cursor-pointer hover:bg-slate-50"
                  onClick={() => {
                    if (tempSelectedProjectIds.includes(project.id)) {
                      setTempSelectedProjectIds(prev => prev.filter(id => id !== project.id));
                    } else {
                      setTempSelectedProjectIds(prev => [...prev, project.id]);
                    }
                  }}
                >
                  <Checkbox 
                    checked={tempSelectedProjectIds.includes(project.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setTempSelectedProjectIds(prev => [...prev, project.id]);
                      } else {
                        setTempSelectedProjectIds(prev => prev.filter(id => id !== project.id));
                      }
                    }}
                  />
                  <span className="text-sm font-medium text-black flex-1">{project.name}</span>
                </div>
                {index < projects.length - 1 && (
                  <div className="mx-3">
                    <div className="w-full" style={{ borderBottom: '0.5px solid black' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Modal */}
      <Dialog open={!!selectedTicketForModal} onOpenChange={(open) => !open && setSelectedTicketForModal(null)}>
        <DialogContent className="max-w-[400px] p-0 gap-0 overflow-hidden bg-transparent border-0 shadow-none">
          {selectedTicketForModal && (
            <div className="flex justify-center">
              <MiniTicketCard
                ticket={selectedTicketForModal}
                project={projects.find(p => p.id === selectedTicketForModal.projectId)}
                organizationId={organizationId}
                className="calendar-mini-ticket-card"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
